import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/documentation/documentation/node_modules/gatsby-theme-doctornpm/src/components/layout.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h3>{`Description`}</h3>
    <p>{`npm gets its config settings from the command line, environment variables,
and `}<inlineCode parentName="p">{`npmrc`}</inlineCode>{` files.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`npm config`}</inlineCode>{` command can be used to update and edit the contents of the
user and global npmrc files.`}</p>
    <p>{`For a list of available configuration options, see
`}<a parentName="p" {...{
        "href": "/cli/v8/using-npm/config"
      }}>{`config`}</a>{`.`}</p>
    <h3>{`Files`}</h3>
    <p>{`The four relevant files are:`}</p>
    <ul>
      <li parentName="ul">{`per-project config file (/path/to/my/project/.npmrc)`}</li>
      <li parentName="ul">{`per-user config file (~/.npmrc)`}</li>
      <li parentName="ul">{`global config file ($PREFIX/etc/npmrc)`}</li>
      <li parentName="ul">{`npm builtin config file (/path/to/npm/npmrc)`}</li>
    </ul>
    <p>{`All npm config files are an ini-formatted list of `}<inlineCode parentName="p">{`key = value`}</inlineCode>{` parameters.
Environment variables can be replaced using `}<inlineCode parentName="p">{`\${VARIABLE_NAME}`}</inlineCode>{`. For
example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`prefix = \${HOME}/.npm-packages
`}</code></pre>
    <p>{`Each of these files is loaded, and config options are resolved in priority
order.  For example, a setting in the userconfig file would override the
setting in the globalconfig file.`}</p>
    <p>{`Array values are specified by adding "[]" after the key name. For example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`key[] = "first value"
key[] = "second value"
`}</code></pre>
    <h4>{`Comments`}</h4>
    <p>{`Lines in `}<inlineCode parentName="p">{`.npmrc`}</inlineCode>{` files are interpreted as comments when they begin with a
`}<inlineCode parentName="p">{`;`}</inlineCode>{` or `}<inlineCode parentName="p">{`#`}</inlineCode>{` character. `}<inlineCode parentName="p">{`.npmrc`}</inlineCode>{` files are parsed by
`}<a parentName="p" {...{
        "href": "https://github.com/npm/ini"
      }}>{`npm/ini`}</a>{`, which specifies this comment syntax.`}</p>
    <p>{`For example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`# last modified: 01 Jan 2016
; Set a new registry for a scoped package
@myscope:registry=https://mycustomregistry.example.org
`}</code></pre>
    <h4>{`Per-project config file`}</h4>
    <p>{`When working locally in a project, a `}<inlineCode parentName="p">{`.npmrc`}</inlineCode>{` file in the root of the
project (ie, a sibling of `}<inlineCode parentName="p">{`node_modules`}</inlineCode>{` and `}<inlineCode parentName="p">{`package.json`}</inlineCode>{`) will set
config values specific to this project.`}</p>
    <p>{`Note that this only applies to the root of the project that you're running
npm in.  It has no effect when your module is published.  For example, you
can't publish a module that forces itself to install globally, or in a
different location.`}</p>
    <p>{`Additionally, this file is not read in global mode, such as when running
`}<inlineCode parentName="p">{`npm install -g`}</inlineCode>{`.`}</p>
    <h4>{`Per-user config file`}</h4>
    <p><inlineCode parentName="p">{`$HOME/.npmrc`}</inlineCode>{` (or the `}<inlineCode parentName="p">{`userconfig`}</inlineCode>{` param, if set in the environment or on
the command line)`}</p>
    <h4>{`Global config file`}</h4>
    <p><inlineCode parentName="p">{`$PREFIX/etc/npmrc`}</inlineCode>{` (or the `}<inlineCode parentName="p">{`globalconfig`}</inlineCode>{` param, if set above): This file
is an ini-file formatted list of `}<inlineCode parentName="p">{`key = value`}</inlineCode>{` parameters.  Environment
variables can be replaced as above.`}</p>
    <h4>{`Built-in config file`}</h4>
    <p><inlineCode parentName="p">{`path/to/npm/itself/npmrc`}</inlineCode></p>
    <p>{`This is an unchangeable "builtin" configuration file that npm keeps
consistent across updates.  Set fields in here using the `}<inlineCode parentName="p">{`./configure`}</inlineCode>{`
script that comes with npm.  This is primarily for distribution maintainers
to override default configs in a standard and consistent manner.`}</p>
    <h3>{`Auth related configuration`}</h3>
    <p>{`The settings `}<inlineCode parentName="p">{`_auth`}</inlineCode>{`, `}<inlineCode parentName="p">{`_authToken`}</inlineCode>{`, `}<inlineCode parentName="p">{`username`}</inlineCode>{` and `}<inlineCode parentName="p">{`_password`}</inlineCode>{` must all be
scoped to a specific registry. This ensures that `}<inlineCode parentName="p">{`npm`}</inlineCode>{` will never send
credentials to the wrong host.`}</p>
    <p>{`In order to scope these values, they must be prefixed by a URI fragment.
If the credential is meant for any request to a registry on a single host,
the scope may look like `}<inlineCode parentName="p">{`//registry.npmjs.org/:`}</inlineCode>{`. If it must be scoped to a
specific path on the host that path may also be provided, such as
`}<inlineCode parentName="p">{`//my-custom-registry.org/unique/path:`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{}}>{`; bad config
_authToken=MYTOKEN

; good config
@myorg:registry=https://somewhere-else.com/myorg
@another:registry=https://somewhere-else.com/another
//registry.npmjs.org/:_authToken=MYTOKEN
; would apply to both @myorg and @another
; //somewhere-else.com/:_authToken=MYTOKEN
; would apply only to @myorg
//somewhere-else.com/myorg/:_authToken=MYTOKEN1
; would apply only to @another
//somewhere-else.com/another/:_authToken=MYTOKEN2
`}</code></pre>
    <h3>{`See also`}</h3>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/configuring-npm/folders"
        }}>{`npm folders`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/commands/npm-config"
        }}>{`npm config`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/using-npm/config"
        }}>{`config`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/configuring-npm/package-json"
        }}>{`package.json`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/commands/npm"
        }}>{`npm`}</a></li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      